/**
 * Step 5 Mobile: Supplementary Income
 * Income items as cards with key info visible.
 */

import { useForm, useFieldArray } from 'react-hook-form';
import type { Path } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { type Step5SupplementaryIncomeFormData, Step5Schema, type AllStepFormData } from '../../types/wizard';
import { WizardMobileShell } from './WizardMobileShell';

interface Step5Props {
  initialData?: Partial<AllStepFormData>;
  onNext: (data: Step5SupplementaryIncomeFormData) => void;
  onSave: (data: Step5SupplementaryIncomeFormData) => void;
  onBack?: () => void;
  lastSaved?: string;
}

const incomeTypes: Record<string, string> = { SALARY: 'Salary', OTHER: 'Other Income' };

export default function Step5SupplementaryIncomeMobile({ initialData, onNext, onSave, onBack, lastSaved }: Readonly<Step5Props>) {
  const personCount = initialData?.planType === 'JOINT' ? 2 : 1;

  const { register, handleSubmit, watch, setValue, control } = useForm<Step5SupplementaryIncomeFormData>({
    resolver: zodResolver(Step5Schema),
    defaultValues: { supplementaryIncome: initialData?.supplementaryIncome || [] },
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'supplementaryIncome' });

  const handleBlur = () => onSave(watch());

  const addItem = () => {
    append({
      incomeId: crypto.randomUUID(),
      type: 'SALARY',
      description: '',
      grossAnnualAmount: 0,
      startYear: 1,
      inflationLinked: true,
      personIndex: 0,
    });
  };

  return (
    <WizardMobileShell step={5} totalSteps={10} title="Income" onBack={onBack} onContinue={() => handleSubmit(onNext)()} lastSaved={lastSaved}>
      <div className="mobile-step-form">
        <p className="mobile-step-desc">Add any other sources of regular income beyond your retirement savings.</p>
        {fields.map((field, i) => {
          const v = watch('supplementaryIncome')[i];
          return (
            <div key={field.id} className="mobile-person-card">
              {personCount > 1 && (
                <div className="mobile-segmented-control" style={{ marginBottom: 4 }}>
                  {[{ v: 0, l: initialData?.persons?.[0]?.fullName || 'Person 1' }, { v: 1, l: initialData?.persons?.[1]?.fullName || 'Person 2' }].map(o => (
                    <label key={o.v} className={`mobile-segment${(v?.personIndex ?? 0) === o.v ? ' mobile-segment--active' : ''}`}>
                      <input type="radio" className="mobile-segment-input" checked={(v?.personIndex ?? 0) === o.v} onChange={() => { setValue(`supplementaryIncome.${i}.personIndex` as Path<Step5SupplementaryIncomeFormData>, o.v); handleBlur(); }} />
                      <span>{o.l}</span>
                    </label>
                  ))}
                </div>
              )}
              <div className="mobile-segmented-control" style={{ marginBottom: 0 }}>
                {Object.entries(incomeTypes).map(([k, label]) => (
                  <label key={k} className={`mobile-segment${(v?.type || 'SALARY') === k ? ' mobile-segment--active' : ''}`}>
                    <input type="radio" className="mobile-segment-input" value={k} {...register(`supplementaryIncome.${i}.type` as Path<Step5SupplementaryIncomeFormData>)} onChange={e => { void register(`supplementaryIncome.${i}.type` as Path<Step5SupplementaryIncomeFormData>).onChange(e); handleBlur(); }} />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
              <input type="text" className="mobile-input" placeholder="Description" {...register(`supplementaryIncome.${i}.description` as Path<Step5SupplementaryIncomeFormData>)} onBlur={handleBlur} />
              <label className="mobile-label">Gross annual amount (£)</label>
              <input type="number" className="mobile-input" {...register(`supplementaryIncome.${i}.grossAnnualAmount` as Path<Step5SupplementaryIncomeFormData>, { valueAsNumber: true })} onBlur={handleBlur} />
              <label className="mobile-label">Start year</label>
              <input type="number" className="mobile-input" min={1} max={100} {...register(`supplementaryIncome.${i}.startYear` as Path<Step5SupplementaryIncomeFormData>, { valueAsNumber: true })} onBlur={handleBlur} />
              <label className="auth-mobile__checkbox">
                <input type="checkbox" {...register(`supplementaryIncome.${i}.inflationLinked` as Path<Step5SupplementaryIncomeFormData>)} onChange={e => { void register(`supplementaryIncome.${i}.inflationLinked` as Path<Step5SupplementaryIncomeFormData>).onChange(e); handleBlur(); }} />
                <span>Inflation-linked</span>
              </label>
              <button type="button" className="mobile-holding-card__delete" onClick={() => remove(i)}>✕ Remove</button>
            </div>
          );
        })}
        <button type="button" className="mobile-btn mobile-btn--outline mobile-btn--full" onClick={addItem}>+ Add Income</button>
      </div>
    </WizardMobileShell>
  );
}
